/**
 * HOOK: useCoins
 * 
 * Gestiona:
 * - Obtener saldo de coins
 * - Descontar coins post-cálculo
 * - Validar coins suficientes
 * - Manejar cálculo gratis mensual
 */

import { useState, useEffect, useCallback } from 'react'

export function useCoins(userRef) {
  const [coinsBalance, setCoinsBalance] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [freeCalculationUsed, setFreeCalculationUsed] = useState(false)
  const [coinsExpireAt, setCoinsExpireAt] = useState(null)

  /**
   * Obtener saldo de coins desde servidor
   */
  const fetchCoinsBalance = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('userToken')
      const response = await fetch('/api/coins/balance', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      })
      const data = await response.json()

      if (!data.success) {
        setError(data.error || 'Error obteniendo saldo')
        setCoinsBalance(0)
        return
      }

      setCoinsBalance(data.coins_balance || 0)
      setFreeCalculationUsed(data.free_calculation_used_this_month || false)
      setCoinsExpireAt(data.coins_expire_at)
    } catch (err) {
      console.error('❌ Error fetching coins balance:', err)
      setError('Error obteniendo saldo de coins')
    } finally {
      setIsLoading(false)
    }
  }, [userRef])

  // Cargar saldo al montar o cambiar CUIT
  useEffect(() => {
    fetchCoinsBalance()
  }, [fetchCoinsBalance])

  /**
   * Descontar coins después de un cálculo
   */
  const deductCoins = useCallback(async (coinsToDeduct, calculationType, calculationId) => {
    if (!userRef) {
      setError('Usuario no proporcionado')
      return false
    }

    if (coinsBalance < coinsToDeduct && !freeCalculationUsed) {
      setError('Saldo insuficiente de coins')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('userToken')
      const response = await fetch('/api/coins/consume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          userRef,
          coins_to_consume: coinsToDeduct,
          calculation_type: calculationType,
          calculation_id: calculationId
        })
      })

      const data = await response.json()

      if (!data.success) {
        setError(data.error || 'Error deduciendo coins')
        return false
      }

      // Actualizar balance local
      setCoinsBalance(data.coins_balance_after)
      setFreeCalculationUsed(true)

      return true
    } catch (err) {
      console.error('❌ Error deducting coins:', err)
      setError('Error deduciendo coins')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [userRef, coinsBalance, freeCalculationUsed])

  /**
   * Consumir coins ANTES del cálculo (server-side).
   * Retorna: { success, error, coinsBalanceAfter }
   * Si error = 'Saldo insuficiente', el frontend debe mostrar modal de compra.
   */
  const consumeCoins = useCallback(async (coinsToConsume, calculationType = 'standard') => {
    if (!userRef) {
      return { success: false, error: 'Usuario no proporcionado' }
    }

    setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('userToken')
      const response = await fetch('/api/coins/consume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          userRef,
          coins_to_consume: coinsToConsume,
          calculation_type: calculationType
        })
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 402) {
          return {
            success: false,
            error: data.message || 'Saldo insuficiente',
            coinsBalance: Number(data?.context?.current_balance || 0),
            coinsNeeded: Number(data?.context?.required_coins || coinsToConsume),
            errorCode: data?.error_code || 'INSUFFICIENT_COINS'
          }
        }
        return { success: false, error: data.error || 'Error al consumir coins' }
      }

      setCoinsBalance(data.coins_balance_after)
      setFreeCalculationUsed(true)

      // Notificar a otros componentes (App.jsx header) del nuevo saldo
      window.dispatchEvent(new CustomEvent('coins-consumed', {
        detail: { balance: data.coins_balance_after }
      }))

      return { success: true, coinsBalanceAfter: data.coins_balance_after }
    } catch (err) {
      console.error('❌ Error consumiendo coins:', err)
      return { success: false, error: 'Error de conexión al consumir coins' }
    } finally {
      setIsLoading(false)
    }
  }, [userRef])

  /**
   * Validar si usuario puede calcular
   * Retorna: { canCalculate, reason }
   */
  const validateCanCalculate = useCallback((coinsRequired = 3) => {
    // Si tiene coins suficientes
    if (coinsBalance >= coinsRequired) {
      return { canCalculate: true }
    }

    // Si no usó el cálculo gratis este mes
    if (!freeCalculationUsed) {
      return { canCalculate: true, isFree: true }
    }

    // No puede calcular
    return {
      canCalculate: false,
      reason: 'Saldo insuficiente. Compra coins para continuar.',
      coinsNeeded: coinsRequired,
      coinsHave: coinsBalance
    }
  }, [coinsBalance, freeCalculationUsed])

  /**
   * Obtener días hasta expiración de coins
   */
  const getDaysUntilExpiration = useCallback(() => {
    if (!coinsExpireAt) return null

    const expirationDate = new Date(coinsExpireAt)
    const today = new Date()
    const diffTime = expirationDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays > 0 ? diffDays : 0
  }, [coinsExpireAt])

  return {
    // Estado
    coinsBalance,
    isLoading,
    error,
    freeCalculationUsed,
    coinsExpireAt,
    daysUntilExpiration: getDaysUntilExpiration(),

    // Métodos
    fetchCoinsBalance,
    deductCoins,
    consumeCoins,
    validateCanCalculate
  }
}
